import type { LucideIcon } from 'lucide-react';
import { Upload, Database, Mail, Cable, Globe, Building2 } from 'lucide-react';
import type { ConnectorType, HubspotObjectType } from '@loader/shared-types';

export type { ConnectorType } from '@loader/shared-types';

export interface ConnectorOption {
  type: ConnectorType;
  label: string;
  description: string;
  icon: LucideIcon;
  available: boolean;
  /** Named integrations (HubSpot, Salesforce) show alongside Nango in the top section */
  named?: boolean;
  /** Logo URL for named integrations (rendered as <img> instead of Lucide icon) */
  logo?: string;
  /** Integration connection provider name (e.g. 'hubspot') — used to hide from picker when connected */
  providerName?: string;
}

/** HubSpot sprocket logo (official PNG from brand assets) */
const HUBSPOT_LOGO = 'https://www.hubspot.com/hubfs/HubSpot_Logos/HubSpot-Inversed-Favicon.png';

export const CONNECTOR_OPTIONS: ConnectorOption[] = [
  { type: 'hubspot_direct', label: 'HubSpot', description: 'Pull contacts, companies, deals & more directly from HubSpot', icon: Building2, available: true, named: true, logo: HUBSPOT_LOGO, providerName: 'hubspot' },
  { type: 'post_api', label: 'POST API', description: 'Push data via HTTP POST requests', icon: Upload, available: true },
  { type: 'nango', label: 'Integrations', description: 'Connect 250+ apps via OAuth — HubSpot, Salesforce, Shopify, and more', icon: Cable, available: true },
  { type: 'http_fetch', label: 'HTTP API', description: 'Pull data from any REST API on a schedule', icon: Globe, available: true },
  { type: 'sql', label: 'SQL Query', description: 'Query a remote database on a schedule', icon: Database, available: true },
  { type: 'email', label: 'Email', description: 'Receive data via email attachments', icon: Mail, available: false },
];

export const CONNECTOR_META = Object.fromEntries(
  CONNECTOR_OPTIONS.map((o) => [o.type, o])
) as Record<ConnectorType, ConnectorOption>;

// ============================================================================
// Native Integration Provider Registry
// ============================================================================

/** Available sync for a native integration (object types the provider exposes) */
export interface AvailableSync {
  key: string;
  label: string;
}

/**
 * Native integration provider definition.
 *
 * To add a new native integration (e.g. Salesforce):
 * 1. Add an entry here with connectorType and objectTypes
 * 2. Add a ConnectorType enum value + config interface in shared-types
 * 3. Add a fetch service in duckdb-api
 * 4. Add an OAuth route in api
 * 5. Add a CONNECTOR_OPTIONS entry above
 */
export interface NativeProviderDefinition {
  /** ConnectorType used by table_connectors (e.g. 'hubspot_direct') */
  connectorType: ConnectorType;
  /** Available object types / syncs for this provider */
  objectTypes: AvailableSync[];
}

export const NATIVE_PROVIDERS: Record<string, NativeProviderDefinition> = {
  hubspot: {
    connectorType: 'hubspot_direct',
    objectTypes: [
      { key: 'contacts', label: 'Contacts' },
      { key: 'companies', label: 'Companies' },
      { key: 'deals', label: 'Deals' },
      { key: 'tickets', label: 'Tickets' },
      { key: 'calls', label: 'Calls' },
      { key: 'emails', label: 'Emails' },
      { key: 'meetings', label: 'Meetings' },
      { key: 'notes', label: 'Notes' },
      { key: 'tasks', label: 'Tasks' },
      { key: 'products', label: 'Products' },
      { key: 'line_items', label: 'Line Items' },
      { key: 'quotes', label: 'Quotes' },
      { key: 'owners', label: 'Owners' },
      { key: 'deal_pipelines', label: 'Deal Pipelines + Stages' },
    ] satisfies { key: HubspotObjectType; label: string }[],
  },
};
